import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircleIcon, ArrowRightIcon, UsersIcon, ShieldCheckIcon, MapPinIcon, CheckCircle2Icon, ChevronDownIcon, StarIcon, CarIcon, CheckIcon, PlaneIcon, HomeIcon, RouteIcon } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
export function IbizaGroupTransfer() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [showStickyBar, setShowStickyBar] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    document.title = 'Ibiza Group Transfer | Group Vans in Ibiza with Fixed Price';
    const metaDesc = document.querySelector('meta[name="description"]');
    const descContent = 'Book group transfers in Ibiza for families, friends, weddings and events. Group vans, airport pickups, villa transfers and fixed-price WhatsApp booking.';
    if (metaDesc) {
      metaDesc.setAttribute('content', descContent);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = descContent;
      document.head.appendChild(meta);
    }
    window.scrollTo(0, 0);
  }, []);
  const whatsappUrl = 'https://wa.me/34613756211?text=Hello%2C%20I%20need%20a%20group%20transfer%20in%20Ibiza.';
  const faqs = [{
    q: 'How many passengers fit in one Ibiza group van?',
    a: 'Usually up to 8 passengers with luggage, depending on the vehicle and luggage load. For larger groups, we arrange multiple vans.'
  }, {
    q: 'Do you offer airport group transfer in Ibiza?',
    a: 'Yes. Airport group transfer is our most requested service. We organise pickups directly at Ibiza Airport with enough space for passengers and luggage.'
  }, {
    q: 'Do you offer villa group transfer in Ibiza?',
    a: 'Yes. We provide direct villa group transport across Ibiza, including check-in arrivals, departures and repeated transfers during the stay.'
  }, {
    q: 'Can you organise wedding or event transport in Ibiza?',
    a: 'Yes. We coordinate group transport for weddings, dinners, corporate events and celebrations with fixed pricing and clear planning.'
  }, {
    q: 'Can you organise multiple vans for large groups in Ibiza?',
    a: 'Yes. We can arrange several vehicles for larger groups, staggered arrivals or guests arriving on different flights.'
  }, {
    q: 'How much does an Ibiza group transfer cost?',
    a: 'Airport group transfers start from 120€. Villa transfers and multi-vehicle setups are quoted individually. We always confirm the fixed price before booking.'
  }];
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <main className="flex-grow pt-20 pb-16 md:pb-0" data-id="element-494">
      {/* 1. Hero */}
      <section className="relative min-h-[70vh] md:min-h-[85vh] flex items-center bg-navy overflow-hidden" data-id="element-495">
        <div className="absolute inset-0 z-0" data-id="element-496">
          <img src="https://cdn.magicpatterns.com/uploads/iPbo7hJEnHtH81JB8m26e5/foto_sprinter.jpg" alt="Premium black group transfer van in Ibiza for 7+ passengers" className="w-full h-full object-cover object-[60%_center] md:object-center opacity-70" data-id="element-497" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/80 via-navy/45 to-navy/15" data-id="element-498" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-5 w-full py-10 md:py-16" data-id="element-499">
          <div className="max-w-xl" data-id="element-500">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="space-y-5 md:space-y-6" data-id="element-501">
              {/* Badges */}
              <div className="flex flex-wrap gap-2" data-id="element-502">
                {['From 120€', '7+ passengers', 'Airport & villa', 'WhatsApp booking'].map((badge, i) => <span key={i} className="inline-flex items-center bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/15 text-white text-[10px] md:text-xs font-semibold tracking-wide" data-id="element-503">
                    <CheckIcon className="w-3 h-3 text-gold-subtle mr-1.5 flex-shrink-0" data-id="element-504" />
                    {badge}
                  </span>)}
              </div>

              {/* H1 */}
              <h1 className="text-[1.65rem] md:text-[3.25rem] font-heading text-white leading-[1.15] md:leading-[1.1]" data-id="element-505">
                Ibiza Group Transfer for 7+ Passengers
              </h1>

              {/* Subheadline */}
              <p className="text-base md:text-lg text-white/85 leading-relaxed max-w-lg" data-id="element-506">
                Fixed-price group vans for airport pickups, villa transfers,
                weddings and events. Fast WhatsApp booking.
              </p>

              {/* Supporting line */}
              <p className="text-sm text-white/50 font-medium" data-id="element-507">
                Better than splitting into multiple taxis.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-1" data-id="element-508">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-509">
                  <span data-id="element-510">Get group transfer quote</span>
                  <WhatsAppIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-511" />
                </a>
                <a href="#pricing" className="w-full sm:w-auto flex items-center justify-center space-x-2 text-white/70 hover:text-gold-subtle px-5 py-3 text-sm font-medium transition-colors" data-id="element-512">
                  <span data-id="element-513">See prices</span>
                  <ArrowRightIcon className="w-4 h-4" data-id="element-514" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <section className="bg-warm-cream border-y border-stone/10 py-7 md:py-9" data-id="element-515">
        <div className="mx-auto max-w-7xl px-5" data-id="element-516">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6" data-id="element-517">
            {[{
            text: 'Group vans for 7+ passengers',
            icon: UsersIcon
          }, {
            text: 'Fixed prices confirmed upfront',
            icon: ShieldCheckIcon
          }, {
            text: 'Airport and villa pickups',
            icon: MapPinIcon
          }, {
            text: 'Multiple vehicles available',
            icon: CarIcon
          }, {
            text: 'Fast WhatsApp coordination',
            icon: MessageCircleIcon
          }].map((item, index) => <div key={index} className="flex flex-col items-center text-center space-y-2" data-id="element-518">
                <div className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center" data-id="element-519">
                  <item.icon className="w-4 h-4 text-gold-subtle" data-id="element-520" />
                </div>
                <span className="text-[11px] md:text-xs font-medium text-slate leading-tight" data-id="element-521">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. Problem / Solution */}
      <section className="py-12 md:py-16 bg-white" data-id="element-522">
        <div className="mx-auto max-w-2xl px-5 text-center" data-id="element-523">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-524">
            <h2 className="text-2xl md:text-[2rem] font-heading text-navy mb-5" data-id="element-525">
              Why group transport in Ibiza gets complicated fast
            </h2>
            <div className="space-y-4 text-base text-slate leading-relaxed mb-6" data-id="element-526">
              <p data-id="element-527">
                Large families, friends, wedding guests and villa groups usually
                face the same problems: not enough space, split taxis, luggage
                issues, long waits and poor coordination.
              </p>
              <p data-id="element-528">
                We organise the right vehicle setup, confirm the fixed price
                upfront and coordinate everything before the journey starts.
              </p>
            </div>

            <div className="bg-navy/5 rounded-xl px-6 py-4 mb-7" data-id="element-529">
              <p className="text-navy font-semibold" data-id="element-530">
                Avoid split taxis, waiting times and unclear planning.
              </p>
              <p className="text-slate text-sm mt-1" data-id="element-531">
                One clear booking. Better coordination. Less stress for
                everyone.
              </p>
            </div>

            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-532">
              <WhatsAppIcon className="w-5 h-5" data-id="element-533" />
              <span data-id="element-534">Get exact group transfer quote</span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* 3b. Comparison: Pre-book vs Taxis */}
      <section className="py-10 md:py-14 bg-warm-cream border-y border-stone/10" data-id="element-535">
        <div className="mx-auto max-w-4xl px-5" data-id="element-536">
          <h2 className="text-2xl md:text-[2rem] font-heading text-navy text-center mb-2.5" data-id="element-537">
            Why pre-book a group transfer instead of taking several taxis in
            Ibiza
          </h2>
          <p className="text-slate text-sm text-center mb-7 max-w-lg mx-auto" data-id="element-538">
            For larger groups, pre-booked transport is usually the easier and
            clearer option.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-539">
            {[{
            title: 'One fixed price',
            desc: 'Instead of several separate taxi fares.',
            icon: ShieldCheckIcon
          }, {
            title: 'Better coordination',
            desc: 'Especially for villas, luggage and multiple arrivals.',
            icon: UsersIcon
          }, {
            title: 'Easier for the whole group',
            desc: 'One plan, one contact point and less waiting.',
            icon: CheckCircle2Icon
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: index * 0.07
          }} className="bg-white rounded-xl border border-stone/10 px-5 py-4 text-center" data-id="element-540">
                <div className="w-9 h-9 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-2.5" data-id="element-541">
                  <item.icon className="w-4 h-4 text-navy/60" data-id="element-542" />
                </div>
                <h3 className="text-[13px] font-medium text-navy mb-1" data-id="element-543">
                  {item.title}
                </h3>
                <p className="text-slate text-[12px] leading-relaxed" data-id="element-544">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 4. Group Transfer Options */}
      <section id="options" className="py-12 md:py-16 bg-white border-b border-stone/10" data-id="element-545">
        <div className="mx-auto max-w-5xl px-5" data-id="element-546">
          <div className="text-center max-w-2xl mx-auto mb-3" data-id="element-547">
            <h2 className="text-2xl md:text-3xl font-heading text-navy mb-3" data-id="element-548">
              Group transfer options in Ibiza
            </h2>
            <p className="text-slate text-sm" data-id="element-549">
              Group vans for airport pickups, villa arrivals, dinners, weddings,
              events and return journeys across the island.
            </p>
          </div>
          <p className="text-center text-navy/50 text-xs font-medium tracking-wide mb-8" data-id="element-550">
            Better than splitting the group into multiple taxis.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5" data-id="element-551">
            {[{
            title: 'Families and holiday groups',
            desc: 'Easier airport and villa transfers with enough luggage space for everyone.'
          }, {
            title: 'Friends staying together',
            desc: 'One group van instead of splitting into several taxis.'
          }, {
            title: 'Wedding guest transport',
            desc: 'Coordinated arrivals and departures with less waiting and clearer planning.'
          }, {
            title: 'Event transport in Ibiza',
            desc: 'Reliable group movement with better timing for celebrations and events.'
          }, {
            title: 'Corporate and business groups',
            desc: 'Professional, efficient transport that is easy to organise quickly.'
          }, {
            title: 'Large villa bookings',
            desc: 'Easier arrivals, departures and repeated group transport during the stay.'
          }].map((block, index) => <div key={index} className="flex items-start gap-2.5 bg-warm-cream/30 border border-stone/8 rounded-xl px-4 py-3.5" data-id="element-552">
                <CheckCircle2Icon className="w-3.5 h-3.5 text-gold-subtle flex-shrink-0 mt-[3px]" data-id="element-553" />
                <div data-id="element-554">
                  <h3 className="text-[13px] text-navy leading-snug mb-0.5" style={{
                fontWeight: 480
              }} data-id="element-555">
                    {block.title}
                  </h3>
                  <p className="text-slate text-[12px] leading-relaxed" data-id="element-556">
                    {block.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. Pricing */}
      <section id="pricing" className="py-14 md:py-20 bg-navy text-white" data-id="element-557">
        <div className="mx-auto max-w-4xl px-5" data-id="element-558">
          <div className="text-center mb-8" data-id="element-559">
            <h2 className="text-2xl md:text-[2rem] font-heading text-white mb-3" data-id="element-560">
              Group transfer prices in Ibiza
            </h2>
            <p className="text-white/60 text-sm max-w-lg mx-auto" data-id="element-561">
              Pricing depends on passenger count, luggage, route and the number
              of vehicles required.
            </p>
          </div>

          {/* Main price highlight */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-8 md:p-10 text-center mb-5" data-id="element-562">
            <p className="text-gold-subtle text-xs font-bold tracking-widest uppercase mb-4" data-id="element-563">
              Most requested option for airport group arrivals
            </p>
            <p className="text-5xl md:text-7xl font-heading text-white mb-1" data-id="element-564">
              120€
            </p>
            <p className="text-white/70 text-sm font-medium mb-4" data-id="element-565">
              Group vans on selected airport routes
            </p>
            <p className="text-gold-subtle font-semibold text-sm" data-id="element-566">
              Fixed price confirmed before booking
            </p>
          </div>

          {/* Sub-pricing cards */}
          <div className="grid grid-cols-3 gap-2 mb-8" data-id="element-567">
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center" data-id="element-568">
              <p className="text-white/40 text-[10px] font-semibold uppercase tracking-wider mb-1" data-id="element-569">
                Airport group
              </p>
              <p className="text-lg font-heading text-white" data-id="element-570">From 120€</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center" data-id="element-571">
              <p className="text-white/40 text-[10px] font-semibold uppercase tracking-wider mb-1" data-id="element-572">
                Villa group
              </p>
              <p className="text-lg font-heading text-white/70" data-id="element-573">Custom quote</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center" data-id="element-574">
              <p className="text-white/40 text-[10px] font-semibold uppercase tracking-wider mb-1" data-id="element-575">
                Multi-vehicle
              </p>
              <p className="text-lg font-heading text-white/70" data-id="element-576">Custom quote</p>
            </div>
          </div>

          {/* Quote CTA */}
          <div className="text-center" data-id="element-577">
            <p className="text-white/50 text-xs mb-5" data-id="element-578">
              No upfront payment · Fixed price confirmed on WhatsApp
            </p>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-579">
              <WhatsAppIcon className="w-5 h-5" data-id="element-580" />
              <span data-id="element-581">Get exact group transfer quote</span>
            </a>
          </div>
        </div>
      </section>

      {/* 6. When to Book */}
      <section className="py-10 md:py-14 bg-warm-cream border-b border-stone/10" data-id="element-582">
        <div className="mx-auto max-w-5xl px-5" data-id="element-583">
          <h2 className="text-2xl md:text-3xl font-heading text-navy text-center mb-7" data-id="element-584">
            When to book a group transfer in Ibiza
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-585">
            {[{
            title: 'Airport arrivals with luggage',
            desc: 'More practical than splitting the group into taxis.'
          }, {
            title: 'Villa check-ins and check-outs',
            desc: 'One coordinated arrival for everyone.'
          }, {
            title: 'Restaurants and nights out',
            desc: 'Simple transport for the whole group.'
          }, {
            title: 'Weddings and special events',
            desc: 'Better planning and easier timing.'
          }, {
            title: 'Several flights or pickups',
            desc: 'We organise multiple vehicles if needed.'
          }, {
            title: 'Family groups of all ages',
            desc: 'Easier, calmer and more comfortable.'
          }].map((useCase, index) => <div key={index} className="bg-white rounded-lg px-4 py-3.5 border border-stone/10" data-id="element-586">
                <div className="flex items-center space-x-2 mb-1" data-id="element-587">
                  <CheckIcon className="w-3.5 h-3.5 text-gold-subtle flex-shrink-0" data-id="element-588" />
                  <span className="text-[13px] text-navy" style={{
                fontWeight: 480
              }} data-id="element-589">
                    {useCase.title}
                  </span>
                </div>
                <p className="text-slate text-[11.5px] leading-relaxed pl-[22px]" data-id="element-590">
                  {useCase.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Why Choose Us */}
      <section className="py-12 md:py-16 bg-white" data-id="element-591">
        <div className="mx-auto max-w-3xl px-5" data-id="element-592">
          <h2 className="text-2xl md:text-3xl font-heading text-navy text-center mb-7" data-id="element-593">
            Why travellers choose our Ibiza group transfer service
          </h2>

          {/* Top 3 highlighted benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2.5" data-id="element-594">
            {[{
            title: 'One booking',
            desc: 'One organiser and one fixed quote.',
            icon: UsersIcon
          }, {
            title: 'Fixed price',
            desc: 'Confirmed before travel.',
            icon: ShieldCheckIcon
          }, {
            title: 'Fast coordination',
            desc: 'WhatsApp planning in minutes.',
            icon: MessageCircleIcon
          }].map((feature, index) => <div key={index} className="bg-navy text-white rounded-xl px-3.5 py-3 flex items-center gap-2.5" data-id="element-595">
                <div className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-596">
                  <feature.icon className="w-3.5 h-3.5 text-gold-subtle" data-id="element-597" />
                </div>
                <div className="min-w-0" data-id="element-598">
                  <h3 className="text-[12.5px] font-medium text-white leading-tight" data-id="element-599">
                    {feature.title}
                  </h3>
                  <p className="text-white/70 text-[11px] leading-snug mt-px" data-id="element-600">
                    {feature.desc}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Secondary benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-id="element-601">
            {[{
            title: 'Enough luggage space',
            desc: 'For airport pickups and villa arrivals.'
          }, {
            title: 'Multiple vehicles',
            desc: 'For larger groups and staggered arrivals.'
          }, {
            title: 'Reliable for events',
            desc: 'Better timing for dinners and weddings.'
          }].map((feature, index) => <div key={index} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-stone/8 bg-warm-cream/30" data-id="element-602">
                <CheckCircle2Icon className="w-3.5 h-3.5 text-gold-subtle flex-shrink-0" data-id="element-603" />
                <div className="min-w-0" data-id="element-604">
                  <h3 className="text-[12px] text-navy leading-tight" style={{
                fontWeight: 500
              }} data-id="element-605">
                    {feature.title}
                  </h3>
                  <p className="text-slate text-[11px] mt-px leading-snug" data-id="element-606">
                    {feature.desc}
                  </p>
                </div>
              </div>)}
          </div>

          <p className="text-center text-navy font-medium text-sm mt-7" data-id="element-607">
            For families, friends and event groups, pre-booked group transport
            is usually the easiest option in Ibiza.
          </p>
        </div>
      </section>

      {/* 8. Popular Routes */}
      <section className="py-12 md:py-16 bg-warm-cream border-y border-stone/10" data-id="element-608">
        <div className="mx-auto max-w-5xl px-5" data-id="element-609">
          <div className="text-center max-w-2xl mx-auto mb-8" data-id="element-610">
            <h2 className="text-2xl md:text-3xl font-heading text-navy mb-3" data-id="element-611">
              Popular group transfer routes in Ibiza
            </h2>
            <p className="text-slate text-sm leading-relaxed" data-id="element-612">
              Fixed-price group transport for the most requested airport, villa
              and event routes across Ibiza.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-613">
            {[{
            route: 'Ibiza Airport → Ibiza Town',
            detail: '15 min · Most popular group route'
          }, {
            route: "Ibiza Airport → Playa d'en Bossa",
            detail: '10 min · Hotels, clubs and beach stays'
          }, {
            route: 'Ibiza Airport → San Antonio',
            detail: '25 min · West coast groups and villas'
          }, {
            route: 'Ibiza Airport → Santa Eulalia',
            detail: '20 min · Family groups and resorts'
          }, {
            route: 'Ibiza Airport → Cala Jondal villas',
            detail: '30 min · Premium villa arrivals'
          }, {
            route: 'Ibiza Airport → San José villas',
            detail: '25 min · Rural villa groups'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.06
          }} className="bg-white rounded-xl border border-stone/10 px-5 py-4 flex items-start space-x-3.5 hover:shadow-sm transition-shadow" data-id="element-614">
                <div className="w-8 h-8 rounded-lg bg-navy/5 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-615">
                  <RouteIcon className="w-4 h-4 text-navy/60" data-id="element-616" />
                </div>
                <div className="min-w-0" data-id="element-617">
                  <h3 className="text-sm font-medium text-navy leading-snug" data-id="element-618">
                    {item.route}
                  </h3>
                  <p className="text-slate text-xs mt-0.5" data-id="element-619">{item.detail}</p>
                  <p className="text-gold-subtle text-[11px] font-medium mt-1.5" data-id="element-620">
                    Fixed price confirmed before booking
                  </p>
                </div>
              </motion.div>)}
          </div>

          <div className="text-center mt-8" data-id="element-621">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-622">
              <WhatsAppIcon className="w-5 h-5" data-id="element-623" />
              <span data-id="element-624">Get a quote for your route</span>
            </a>
            <p className="text-slate text-xs mt-3" data-id="element-625">
              Route not listed? Send your pickup and drop-off and we'll quote
              it.
            </p>
          </div>
        </div>
      </section>

      {/* 9. FAQs */}
      <section className="py-12 md:py-16 bg-warm-white border-y border-stone/10" data-id="element-626">
        <div className="mx-auto max-w-3xl px-5" data-id="element-627">
          <h2 className="text-2xl md:text-3xl font-heading text-navy text-center mb-8" data-id="element-628">
            Frequently asked questions
          </h2>
          <div className="space-y-3" data-id="element-629">
            {faqs.map((faq, index) => <div key={index} className="bg-white border border-stone/10 rounded-xl overflow-hidden" data-id="element-630">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)} className="w-full flex items-center justify-between p-4 md:p-5 text-left" data-id="element-631">
                  <span className="font-semibold text-navy pr-4 text-sm" data-id="element-632">
                    {faq.q}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 text-slate flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} data-id="element-633" />
                </button>
                <AnimatePresence data-id="element-634">
                  {openFaqIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-635">
                      <div className="px-4 md:px-5 pb-4 md:pb-5 text-slate text-sm leading-relaxed" data-id="element-636">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(faqSchema)
      }} data-id="element-637" />
      </section>

      {/* 10. Reviews */}
      <section className="py-12 md:py-16 bg-white border-b border-stone/10" data-id="element-638">
        <div className="mx-auto max-w-5xl px-5" data-id="element-639">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-gold-subtle mb-2" data-id="element-640">
            Trusted by families, villa groups and event bookings
          </p>
          <h2 className="text-2xl md:text-3xl font-heading text-navy text-center mb-8" data-id="element-641">
            What clients say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-642">
            {[{
            text: 'We were 11 people arriving on different flights and everything was coordinated perfectly. Could not have been easier.',
            author: 'Group booking',
            tag: '11 passengers · airport arrival',
            featured: true
          }, {
            text: 'Much easier than splitting into taxis. Clear communication and fixed price from the start.',
            author: 'Family group, London',
            tag: 'Family villa transfer',
            featured: false
          }, {
            text: 'Perfect for our villa check-in. Enough space, on time and very easy to organise.',
            author: 'Guest, Madrid',
            tag: 'Villa check-in · 8 passengers',
            featured: false
          }].map((review, index) => <div key={index} className={`rounded-xl p-6 flex flex-col ${review.featured ? 'bg-navy text-white ring-1 ring-white/10' : 'bg-warm-cream'}`} data-id="element-643">
                <span className={`text-[10px] font-semibold uppercase tracking-wider mb-3 ${review.featured ? 'text-gold-subtle' : 'text-gold-subtle'}`} data-id="element-644">
                  {review.tag}
                </span>
                <div className="flex space-x-0.5 mb-3" data-id="element-645">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-3.5 h-3.5 fill-gold-subtle text-gold-subtle" data-id="element-646" />)}
                </div>
                <p className={`font-medium text-sm mb-4 leading-relaxed flex-grow ${review.featured ? 'text-white' : 'text-navy'}`} data-id="element-647">
                  "{review.text}"
                </p>
                <p className={`text-xs ${review.featured ? 'text-white/60' : 'text-slate'}`} data-id="element-648">
                  — {review.author}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 11. Internal Links */}
      <section className="py-10 md:py-12 bg-warm-white border-b border-stone/10" data-id="element-649">
        <div className="mx-auto max-w-3xl px-5" data-id="element-650">
          <p className="text-sm font-semibold text-navy text-center mb-4" data-id="element-651">
            Need a direct airport pickup or villa arrival transfer instead?
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto" data-id="element-652">
            <Link to="/ibiza-airport-transfer" className="flex items-center space-x-3 bg-white border border-stone/10 rounded-lg px-5 py-4 hover:shadow-md hover:border-stone/20 transition-all group" data-id="element-653">
              <PlaneIcon className="w-4 h-4 text-gold-subtle flex-shrink-0" data-id="element-654" />
              <span className="font-medium text-navy text-sm flex-grow" data-id="element-655">
                Ibiza Airport Transfer
              </span>
              <ArrowRightIcon className="w-4 h-4 text-slate group-hover:text-navy transition-colors" data-id="element-656" />
            </Link>
            <Link to="/ibiza-villa-transfer" className="flex items-center space-x-3 bg-white border border-stone/10 rounded-lg px-5 py-4 hover:shadow-md hover:border-stone/20 transition-all group" data-id="element-657">
              <HomeIcon className="w-4 h-4 text-gold-subtle flex-shrink-0" data-id="element-658" />
              <span className="font-medium text-navy text-sm flex-grow" data-id="element-659">
                Ibiza Villa Transfer
              </span>
              <ArrowRightIcon className="w-4 h-4 text-slate group-hover:text-navy transition-colors" data-id="element-660" />
            </Link>
          </div>
        </div>
      </section>

      {/* 12. Final CTA */}
      <section className="py-12 md:py-18 bg-navy relative overflow-hidden" data-id="element-661">
        <div className="relative mx-auto max-w-3xl px-5 text-center z-10" data-id="element-662">
          <h2 className="text-2xl md:text-[2rem] font-heading text-white mb-3 leading-tight" data-id="element-663">
            Book your Ibiza group transfer now
          </h2>
          <p className="text-sm text-white/60 mb-6 max-w-md mx-auto leading-relaxed" data-id="element-664">
            Send your route on WhatsApp and get your fixed quote in minutes.
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20 mb-3" data-id="element-665">
            <WhatsAppIcon className="w-5 h-5" data-id="element-666" />
            <span data-id="element-667">Get group transfer quote</span>
          </a>
          <p className="text-xs text-white/40" data-id="element-668">
            Fast reply · Fixed price · No upfront payment
          </p>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      {showStickyBar && <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/95 backdrop-blur-sm border-t border-stone/15 px-4 py-3" data-id="element-669">
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white w-full py-3.5 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20 text-sm" data-id="element-670">
            <span data-id="element-671">Get group transfer quote</span>
            <WhatsAppIcon className="w-4 h-4" data-id="element-672" />
          </a>
        </div>}
    </main>;
}